import React, { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { AssetUrls } from 'config/assets';
import './EliteUpsellPrompt.css';

// Matches the Elite nav banner (EliteUpsellBanner.tsx) — keep in sync so the
// upsell visuals stay identical across the app.
const BNG_47_SCRYFALL_ID = '546dae51-070c-4f3f-b0b4-6b88ff314af1';

interface EliteUpsellPromptProps {
  onClose: () => void;
  isOpen?: boolean;
  title?: string;
  body?: string;
  emojiRow?: React.ReactNode;
  source?: string;
  learnMoreLabel?: string;
  /** Deprecated alias for `body`, kept for existing call sites. */
  text?: string;
}

const DEFAULT_BODY =
  'Upload custom emoji with TradeMagic Elite. Your emoji become available to the entire community!';

export const EliteUpsellPrompt: React.FC<EliteUpsellPromptProps> = ({
  onClose,
  isOpen = true,
  title,
  body,
  emojiRow,
  source = 'custom-emoji',
  learnMoreLabel = 'Learn More',
  text,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const resolvedBody = body ?? text ?? DEFAULT_BODY;
  const bgUrl = AssetUrls.cards.getCardImage(BNG_47_SCRYFALL_ID, 'art_crop');

  return createPortal(
    <div
      className="elite-upsell-modal-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="elite-upsell-modal"
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'elite-upsell-modal-title' : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="elite-upsell-modal__header"
          style={{ backgroundImage: `url(${bgUrl})` }}
        >
          <div className="elite-upsell-modal__header-overlay" />
          <div className="elite-upsell-modal__header-badge">
            <img
              src={AssetUrls.icons.elite.badge}
              alt=""
              className="elite-upsell-modal__header-badge-icon"
            />
            <span className="elite-upsell-modal__header-badge-label">Elite</span>
          </div>
        </div>

        <div className="elite-upsell-modal__body">
          {title && (
            <h2 id="elite-upsell-modal-title" className="elite-upsell-modal__title">
              {title}
            </h2>
          )}
          <p className="elite-upsell-modal__text">{resolvedBody}</p>
          {emojiRow && <div className="elite-upsell-modal__emoji-row">{emojiRow}</div>}
          <Link
            to={`/elite?source=${source}`}
            className="elite-upsell-modal__cta"
            onClick={onClose}
          >
            {learnMoreLabel}
          </Link>
          <button
            type="button"
            className="elite-upsell-modal__dismiss"
            onClick={onClose}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
